import { type BaseQueryFn } from '@reduxjs/toolkit/query/react'
import { createApi } from '@reduxjs/toolkit/query/react'
import { type AxiosError, type AxiosRequestConfig } from 'axios'
import { captureException } from '@sentry/react'
import { ServiceErrorHandler } from '@/connect-types/backend/service'
import { customAxios } from '@/utils/axiosHelper'
import config from '@/config'
import { currencyLocale } from '@/utils/common'
import {
  type PurchaseSmsCreditsResponse,
  type AddonFeaturesType,
  type BillingType,
  type IncreaseProductQuantitySuccessResponse,
  type BillingMetadataResponse,
  type SubscriptionOverrideRequest,
  type SubscriptionOverrideResponse,
  type AdminSubscriptionOverrideResponse,
} from './billing.types'

const axiosBaseQuery =
  (
    { baseUrl }: { baseUrl: string } = { baseUrl: '' }
  ): BaseQueryFn<{
    url: string
    method: AxiosRequestConfig['method']
    data?: AxiosRequestConfig['data']
    params?: AxiosRequestConfig['params']
  }> =>
  async ({ url, method, data, params }) => {
    try {
      const result = await customAxios({
        url: baseUrl + url,
        method,
        data,
        params,
      })
      return { data: result.data }
    } catch (axiosError) {
      try {
        const err = axiosError as AxiosError

        if (method !== 'GET' && err.response.status !== 401) {
          ServiceErrorHandler(axiosError)
        }

        return {
          error: {
            status: err.response?.status,
            data: err.response?.data || err.message,
          },
        }
      } catch (e) {
        captureException(e)
      }
    }
  }

const billingApi = createApi({
  reducerPath: 'billing',
  refetchOnReconnect: true,
  baseQuery: axiosBaseQuery({ baseUrl: config.url.billing }),
  tagTypes: ['bill', 'addon-metadata'],
  endpoints: (build) => ({
    get: build.query<BillingType, { orgId: string }>({
      providesTags: (_1, _2, meta) =>
        _1 ? [{ type: 'bill', id: meta.orgId }] : [],

      query: ({ orgId }) => ({
        method: 'GET',
        url: `/public/organisations/${orgId}/subscription`,
      }),
      transformResponse(data: BillingType) {
        data.needs_venue_upgrade = 0
        data.needs_contact_upgrade = 0
        data.numberFormatter = currencyLocale(data.subscription.currency)

        return data
      },
    }),
    /**
     * Starts a 30-day Stripe trial for a freshly created organisation
     * (org-admin auth — the requesting user has this immediately after
     * creating the org, since they became its owner). Local subscription
     * rows lag behind this call via the `customer.subscription.created`
     * webhook, so `get` above may not reflect it right away.
     */
    createTrialSubscription: build.mutation<
      {
        message: string
        stripe_customer_id: string
        stripe_subscription_id: string
      },
      {
        orgId: string
        data: {
          package: 'full_bhuna'
          venues: number
          referral_org_id?: string
        }
      }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/subscription/trial`,
        data,
      }),
    }),

    assignVenueToCode: build.mutation<
      BillingType,
      { orgId: string; code: AddonFeaturesType; serial: string }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId, serial, code }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/subscription/venues`,
        data: {
          serial,
          code,
        },
      }),
    }),
    removeVenueFromCode: build.mutation<
      BillingType,
      { orgId: string; code: AddonFeaturesType; serial: string }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId, serial, code }) => ({
        method: 'DELETE',
        url: `/organisations/${orgId}/subscription/venues`,

        data: {
          serial,
          code,
        },
      }),
    }),

    removeSubscription: build.mutation<
      unknown,
      {
        orgId: string
      }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId }) => ({
        method: 'DELETE',
        url: `/admin/organisations/${orgId}/subscription`,
      }),
    }),
    unlinkStripeSubscription: build.mutation<
      unknown,
      {
        orgId: string
      }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId }) => ({
        method: 'POST',
        url: `/admin/organisations/${orgId}/subscription/unlink-stripe-subscription`,
      }),
    }),

    linkStripeSubscription: build.mutation<
      unknown,
      {
        subscription_id: string
        orgId: string
      }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId, subscription_id }) => ({
        method: 'POST',
        url: `/admin/organisations/${orgId}/subscription/link-stripe-subscription`,

        data: {
          subscription_id,
        },
      }),
    }),
    stripePortal: build.query<{ url: string }, { org_id: string }>({
      query: ({ org_id }) => ({
        url: `/organisations/${org_id}/subscription/stripe-portal`,
        method: 'POST',
      }),
    }),
    createCustomSubscription: build.mutation<
      unknown,
      {
        orgId: string
        data: {
          addons: { code: string; quantity: number }[]
          expires_at: Date
          confirm_overwrite: boolean | undefined
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/admin/organisations/${orgId}/subscription`,

        data,
      }),
    }),
    setSubscriptionOverride: build.mutation<
      SubscriptionOverrideResponse,
      { orgId: string; data: SubscriptionOverrideRequest }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/admin/organisations/${orgId}/subscription-override`,
        data,
      }),
    }),
    clearSubscriptionOverride: build.mutation<
      SubscriptionOverrideResponse,
      { orgId: string }
    >({
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId }) => ({
        method: 'DELETE',
        url: `/admin/organisations/${orgId}/subscription-override`,
      }),
    }),
    getSubscriptionOverride: build.query<
      AdminSubscriptionOverrideResponse,
      { orgId: string }
    >({
      providesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],

      query: ({ orgId }) => ({
        method: 'GET',
        url: `/admin/organisations/${orgId}/subscription-override`,
      }),
    }),
    /**
     * SMS
     */
    smsMetadata: build.query<
      {
        product: {
          name: string
          default_price: {
            unit_amount: number
            currency: string
            tax_behavior: string
          }
        }
        allowed_range: { min: number; max: number }
        payment_methods: {
          id: string
          type: string
          card: {
            brand: string
            exp_month: string
            exp_year: string
            last4: string
          }
        }[]
      },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        url: `/organisations/${orgId}/subscription/stripe/purchase-sms-credits/metadata`,
        method: 'get',
      }),
    }),
    purchaseSmsCredits: build.mutation<
      PurchaseSmsCreditsResponse,
      {
        orgId: string
        data: {
          quantity: number
          payment_method_id: string
        }
      }
    >({
      query: ({ orgId, data }) => ({
        url: `/organisations/${orgId}/subscription/stripe/purchase-sms-credits`,
        method: 'post',
        data,
      }),
    }),
    /**
     * SMS END
     */

    /**
     * Access validation check for increase-product-quantity.
     * Returns 200 if the user has a valid Stripe subscription, 404/400 otherwise.
     */
    increaseProductQuantityMetadata: build.query<
      { message: string },
      { orgId: string }
    >({
      query: ({ orgId }) => ({
        method: 'GET',
        url: `/organisations/${orgId}/subscription/increase-product-quantity/metadata`,
      }),
    }),

    /**
     * Access validation check for upgrade-product.
     * Returns 200 if the user has a valid Stripe subscription, 404/400 otherwise.
     */
    upgradeProductMetadata: build.query<{ message: string }, { orgId: string }>(
      {
        query: ({ orgId }) => ({
          method: 'GET',
          url: `/organisations/${orgId}/subscription/upgrade-product/metadata`,
        }),
      }
    ),

    /**
     * Increments an addon on a stripe subscription.
     */
    increaseProductQuantity: build.mutation<
      IncreaseProductQuantitySuccessResponse,
      {
        orgId: string
        data: {
          idempotency_key: string
          code: string
          increase_by: number
          preview: boolean
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/subscription/increase-product-quantity`,
        data,
      }),
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],
    }),

    upgradeProduct: build.mutation<
      IncreaseProductQuantitySuccessResponse,
      {
        orgId: string
        data: {
          idempotency_key: string
          code: string
          preview: boolean
        }
      }
    >({
      query: ({ orgId, data }) => ({
        method: 'POST',
        url: `/organisations/${orgId}/subscription/upgrade-product`,
        data,
      }),
      invalidatesTags: (_1, _2, meta) => [{ type: 'bill', id: meta.orgId }],
    }),

    publicMetadataAddons: build.query<BillingMetadataResponse, {}>({
      query: () => ({
        url: '/public/metadata/addons',
        method: 'get',
      }),
      providesTags: ['addon-metadata'],
    }),
  }),
})

export default billingApi
